import { RedIcon } from "@/components/styled";
import { UserSelect } from "@/containers/Project/components";
import { CONTENT_WIDTH, GUTTER_SIZE } from "@/enums";
import { getLimitedListFromTemplateRoles } from "@/helpers";
import { IProjectTeamRole, ITemplateRolesMapping } from "@/interfaces";
import { useProjectContext } from "@/providers";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Col,
  Form,
  Popconfirm,
  Row,
  Space,
  Switch,
  Tooltip,
} from "antd";
import { useIntl } from "react-intl";

interface IProps {
  teamRole: IProjectTeamRole | undefined;
  editMode: boolean;
  handleDelete(role: IProjectTeamRole): void;
  templateRolesMap: { [key: string]: ITemplateRolesMapping };
}

export const ProjectTeamMemberField = ({
  teamRole,
  editMode,
  handleDelete,
  templateRolesMap,
}: IProps) => {
  const intl = useIntl();
  const { project } = useProjectContext();

  if (!teamRole) return <></>;

  return (
    <Col
      className="pr-1"
      xs={CONTENT_WIDTH.max}
      md={CONTENT_WIDTH.half}
      lg={CONTENT_WIDTH.oneThird}
      xxl={CONTENT_WIDTH.oneQuarter}
    >
      <Row gutter={GUTTER_SIZE.small} wrap={false} align="middle">
        {editMode && (
          <Col>
            <Form.Item label=" ">
              <Popconfirm
                title={intl.formatMessage({
                  id: "projects.deleteThisRole",
                })}
                okText={intl.formatMessage({ id: "general.yes" })}
                cancelText={intl.formatMessage({ id: "general.cancel" })}
                onConfirm={(): void => handleDelete(teamRole)}
              >
                <Button
                  size="large"
                  icon={<RedIcon icon={faXmark as IconProp} />}
                  data-test-id={`delete-role-${teamRole.id}`}
                  type="link"
                />
              </Popconfirm>
            </Form.Item>
          </Col>
        )}
        <Col flex="auto">
          <Form.Item
            label={
              <Space size="small">
                {teamRole.name}
                {teamRole.isMilestoneEditor && (
                  <Tooltip
                    title={intl.formatMessage({
                      id: "projectRoles.milestoneEditor",
                    })}
                  >
                    <span>{"✏️"}</span>
                  </Tooltip>
                )}
              </Space>
            }
            name={[teamRole.id, "assignedUsers"]}
            initialValue={teamRole.assignedUsers || []}
          >
            <UserSelect
              multipleSelect
              showDeletedUsers={false}
              users={teamRole.assignedUsers}
              limitedOptions={
                project.isMigrated && teamRole.limitedListUsers?.length > 0
                  ? teamRole.limitedListUsers
                  : getLimitedListFromTemplateRoles(teamRole, templateRolesMap)
              }
              disabled={!editMode}
              readOnly={!editMode}
              dataTestId={`limited-list-role-${teamRole.roleId}`}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            name={[teamRole.id, "isCoreTeam"]}
            valuePropName="checked"
            initialValue={teamRole.isCoreTeam}
            label={intl.formatMessage({ id: "project.coreTeamToggle" })}
          >
            <Switch
              disabled={!editMode}
              checkedChildren={intl.formatMessage({
                id: "general.yes",
              })}
              unCheckedChildren={intl.formatMessage({
                id: "general.no",
              })}
            />
          </Form.Item>
        </Col>
      </Row>
    </Col>
  );
};
